import { parseMetrics } from './parser.js';
import { DEFAULT_THRESHOLDS, renderTopology, updateRailHints } from './render.js';
import { initAccordions } from './accordion.js';
import { initControls } from './control.js';
import { initFeed } from './feed.js';
import { initFlows } from './flows.js';
import { updatePersonas } from './personas.js';
import { recordTps, renderStats } from './stats.js';
import { initStories } from './stories.js';
import { initTokenLayer, updateTokens } from './tokens.js';
import { updateScoreboard, getCounters } from './scoreboard.js';
import { renderKpiRow } from './kpis.js';
import type { RequestEvent } from './feed.js';

const FRESHNESS_MS = 5000; // NFR-TS-004
const POLL_MS = clampPoll(Number(localStorage.getItem('ts_poll_ms') ?? '3000'));

function clampPoll(ms: number): number {
  if (!Number.isFinite(ms)) return 3000;
  return Math.min(5000, Math.max(1000, Math.trunc(ms))); // ≤5s, ≥1s (FR-TS-095)
}

let lastOk = false;
let lastOkAt = 0;
let lastFeedEvents: RequestEvent[] = [];
let lastConservationCents: number | null = null;
let lastTps = 0;
let lastP95Ms = 0;

async function fetchMetrics(): Promise<string | null> {
  try {
    const r = await fetch('/metrics', { cache: 'no-store' });
    if (!r.ok) throw new Error(`status ${r.status}`);
    const text = await r.text();
    lastOk = true;
    lastOkAt = Date.now();
    return text;
  } catch {
    lastOk = false;
    return null;
  }
}

function updateStaleness(): void {
  const banner = document.querySelector('[data-stale]');
  const age = lastOk ? Date.now() - lastOkAt : Infinity;
  const stale = !lastOk || age > FRESHNESS_MS;
  banner?.setAttribute('data-stale', stale ? 'true' : 'false');
  const ageEl = banner?.querySelector('[data-field="age"]');
  if (ageEl) ageEl.textContent = lastOk ? `${Math.round(age / 1000)}s` : '—';
}

async function tick(): Promise<void> {
  const text = await fetchMetrics();
  updateStaleness();
  // On failure, keep last-known render and mark stale (NFR-TS-013 graceful degradation).
  if (text !== null) {
    const snap = parseMetrics(text);
    renderTopology(document, snap, { thresholds: DEFAULT_THRESHOLDS });
    recordTps(snap.aggregateTps);
    updateRailHints(document, lastFeedEvents);
    renderStats(document, lastFeedEvents, lastConservationCents);
    lastTps = snap.aggregateTps;
    lastP95Ms = snap.banks.length ? Math.max(...snap.banks.map((b) => b.latencyP95 * 1000)) : 0;
    renderKpiRow(document, {
      tps: lastTps,
      p95Ms: lastP95Ms,
      settledCount: getCounters().settled,
      fraudCount: getCounters().fraudBlocked,
      sanctionsCount: getCounters().sanctionsCaught,
    });
  }
  updateStaleness();
}

function start(): void {
  const svgEl = document.querySelector('svg.topology') as SVGSVGElement | null;
  initTokenLayer(document);
  initAccordions(document);
  initControls(document);
  initFeed(document, 1500, ({ events, conservationCents, driftCents }) => {
    lastFeedEvents = events;
    lastConservationCents = conservationCents;
    updatePersonas(document, events);
    updateTokens(svgEl, events);
    const conservedOk = driftCents !== null && Math.abs(driftCents) <= 100; // within €1
    updateScoreboard(document, events, conservedOk);
    renderKpiRow(document, {
      tps: lastTps,
      p95Ms: lastP95Ms,
      settledCount: getCounters().settled,
      fraudCount: getCounters().fraudBlocked,
      sanctionsCount: getCounters().sanctionsCaught,
    });
  });
  initFlows(document);
  initStories(document);
  void tick();
  window.setInterval(() => void tick(), POLL_MS);
}

start();
